import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function SnapshotsDashboard() {
  const [activeTab, setActiveTab] = useState('snapshots'); // 'snapshots' or 'schedules'
  const [snapshots, setSnapshots] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [instances, setInstances] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const instancesRes = await fetch(`${API_URL}/v1/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const instancesData = await instancesRes.json();
      setInstances(instancesData.instances || []);

      if (activeTab === 'snapshots') {
        const snapshotsRes = await fetch(`${API_URL}/v1/snapshots`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const snapshotsData = await snapshotsRes.json();
        setSnapshots(snapshotsData.snapshots || []);
      } else {
        const schedulesRes = await fetch(`${API_URL}/v1/backup-schedules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const schedulesData = await schedulesRes.json();
        setSchedules(schedulesData.schedules || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const createSnapshot = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/snapshots`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instance_id: formData.get('instance_id'),
          name: formData.get('name'),
          description: formData.get('description')
        })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating snapshot: ' + error.message);
    }
  };

  const createSchedule = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/backup-schedules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instance_id: formData.get('instance_id'),
          name: formData.get('name'),
          frequency: formData.get('frequency'),
          retention_days: parseInt(formData.get('retention_days'))
        })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating schedule: ' + error.message);
    }
  };

  const restoreSnapshot = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/snapshots/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          snapshot_id: selectedSnapshot.id,
          name: formData.get('name')
        })
      });
      if (response.ok) {
        setShowRestoreModal(false);
        alert('Instance restoration started! Check your instances list.');
      }
    } catch (error) {
      alert('Error restoring snapshot: ' + error.message);
    }
  };

  const deleteSnapshot = async (snapshotId) => {
    if (!window.confirm('Delete this snapshot? This cannot be undone.')) return;
    try {
      await fetch(`${API_URL}/v1/snapshots/${snapshotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting snapshot: ' + error.message);
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this backup schedule?')) return;
    try {
      await fetch(`${API_URL}/v1/backup-schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting schedule: ' + error.message);
    }
  };

  const getInstanceName = (instanceId) => {
    const instance = instances.find(i => i.id === instanceId);
    return instance ? instance.name : instanceId.substring(0, 8);
  };

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-6">Snapshots & Backups</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('snapshots')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'snapshots'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Snapshots
          </button>
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'schedules'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Backup Schedules
          </button>
        </div>

        {/* Create Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
        >
          + {activeTab === 'snapshots' ? 'Create Snapshot' : 'Create Schedule'}
        </button>

        {/* Snapshots Table */}
        {activeTab === 'snapshots' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Instance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {snapshots.map((snapshot) => (
                  <tr key={snapshot.id}>
                    <td className="px-6 py-4 text-white font-medium">{snapshot.name}</td>
                    <td className="px-6 py-4 text-gray-400">{getInstanceName(snapshot.instance_id)}</td>
                    <td className="px-6 py-4 text-gray-400">{snapshot.size_gb} GB</td>
                    <td className="px-6 py-4 text-gray-400 capitalize">{snapshot.snapshot_type}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        snapshot.status === 'available' ? 'bg-green-900 text-green-300' :
                        snapshot.status === 'creating' ? 'bg-blue-900 text-blue-300' :
                        snapshot.status === 'failed' ? 'bg-red-900 text-red-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {snapshot.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(snapshot.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 space-x-3">
                      {snapshot.status === 'available' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedSnapshot(snapshot);
                              setShowRestoreModal(true);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Restore
                          </button>
                          <button
                            onClick={() => deleteSnapshot(snapshot.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {snapshots.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No snapshots yet. Create your first snapshot to enable point-in-time recovery!
              </div>
            )}
          </div>
        )}

        {/* Backup Schedules Table */}
        {activeTab === 'schedules' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Instance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Frequency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Retention</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Next Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td className="px-6 py-4 text-white font-medium">{schedule.name}</td>
                    <td className="px-6 py-4 text-gray-400">{getInstanceName(schedule.instance_id)}</td>
                    <td className="px-6 py-4 text-gray-400 capitalize">{schedule.frequency}</td>
                    <td className="px-6 py-4 text-gray-400">{schedule.retention_days} days</td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {schedule.next_run ? new Date(schedule.next_run).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        schedule.enabled ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'
                      }`}>
                        {schedule.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => deleteSchedule(schedule.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {schedules.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No backup schedules yet. Create automated backups for your instances!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Snapshot Modal */}
      {showCreateModal && activeTab === 'snapshots' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Create Snapshot</h3>
            
            <form onSubmit={createSnapshot}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Instance</label>
                  <select
                    name="instance_id"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="">Select instance...</option>
                    {instances.filter(i => i.status === 'running').map(instance => (
                      <option key={instance.id} value={instance.id}>{instance.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Snapshot Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="e.g., Before upgrade"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description (optional)</label>
                  <textarea
                    name="description"
                    rows="3"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Snapshot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Schedule Modal */}
      {showCreateModal && activeTab === 'schedules' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Create Backup Schedule</h3>
            
            <form onSubmit={createSchedule}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Instance</label>
                  <select
                    name="instance_id"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="">Select instance...</option>
                    {instances.filter(i => i.status === 'running').map(instance => (
                      <option key={instance.id} value={instance.id}>{instance.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Schedule Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="e.g., Daily backup"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Frequency</label>
                  <select
                    name="frequency"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Retention (days)</label>
                  <input
                    name="retention_days"
                    type="number"
                    min="1"
                    max="365"
                    required
                    defaultValue="7"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Restore Snapshot Modal */}
      {showRestoreModal && selectedSnapshot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Restore Snapshot</h3>
            <p className="text-gray-400 mb-4">
              This will create a new instance from snapshot: <strong className="text-white">{selectedSnapshot.name}</strong>
            </p>
            
            <form onSubmit={restoreSnapshot}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">New Instance Name</label>
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="e.g., Restored instance"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowRestoreModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Restore
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default SnapshotsDashboard;
